<script lang="ts" context="module">
    import { getServerValue } from "$includes/Helpers";
    import Config from "$domain/index";

    //	legacy Safari website push (APNs) needs the brand's Apple config; without
    //	it Safari 16.4+ takes the standard Web Push path like every other browser
    function legacySafari(): boolean {
        return (
            "safari" in window &&
            "pushNotification" in (window as any).safari &&
            !!Config.appleWebPushConfig?.websitePushID
        );
    }

    export async function getPermissionStatus() {
        if (legacySafari()) {
            const { permission } =
                await window.safari.pushNotification.permission(
                    Config.appleWebPushConfig.websitePushID
                );
            return permission;
        } else {
            return Notification.permission;
        }
    }

    async function askPermission() {
        return new Promise(function (resolve, reject) {
            if (legacySafari()) {
                return window.safari.pushNotification.requestPermission(
                    Config.appleWebPushConfig.webServiceURL,
                    Config.appleWebPushConfig.websitePushID,
                    {},
                    resolve
                );
            }

            const permissionResult = Notification.requestPermission(function (
                result
            ) {
                resolve(result);
            });

            if (permissionResult) {
                permissionResult.then(resolve, reject);
            }
        });
    }

    function urlB64ToUint8Array(base64String: string) {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, "+")
            .replace(/_/g, "/");

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    async function subscribeUserToPush() {
        if (legacySafari()) {
            return window.safari.pushNotification.permission(
                Config.appleWebPushConfig.websitePushID
            );
        }

        return navigator.serviceWorker
            .register("/service-worker-push.js")
            .then(function (registration) {
                const subscribeOptions = {
                    userVisibleOnly: true,
                    applicationServerKey: urlB64ToUint8Array(
                        getServerValue("__PUSH_VAPID_KEY__")
                    ),
                };

                return registration.pushManager.subscribe(subscribeOptions);
            });
    }

    async function saveSubscription(data: any) {
        const isSaved = localStorage.getItem("push_sub_saved");
        const savedAt = new Date(localStorage.getItem("push_sub_saved_time"));

        const type =
            legacySafari()
                ? "web-safari"
                : "web";

        const body = JSON.stringify({ type, data: data });

        // if already saved don't spam the server unless last send was more than 5 days ago
        if (
            isSaved &&
            isSaved === body &&
            new Date().getTime() - savedAt.getTime() < 86400000 * 5
        ) {
            return;
        }

        const res = await fetch(
            `${getServerValue("__API_PUBLIC__")}/api/push?domain=${
                Config.domain
            }`,
            {
                method: "POST",
                mode: "cors",
                cache: "no-cache",
                body,
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        if (res.status === 200 || res.status === 201) {
            localStorage.setItem("push_sub_saved", body);
            localStorage.setItem(
                "push_sub_saved_time",
                new Date().toISOString()
            );
        }
    }

    export async function subscribe(): Promise<boolean> {
        const currentStatus = await getPermissionStatus();

        if (currentStatus !== "granted") {
            const permission = await askPermission();
            if (permission !== "granted") {
                return false;
            }
        }

        const endpointData = await subscribeUserToPush();

        await saveSubscription(endpointData);

        return true;
    }
</script>
